import is from '@sindresorhus/is';
import { z } from 'zod';

import type {
    AiTransformationAttributeFilter,
    AiTransformationMatrixGrouping,
    AiTransformationSubject,
} from '../../../data-contract/ai-transformation-strategy';
import { automationScoreLevelSchema } from '../../../data-contract/ai-transformation-strategy';
import type {
    AiTransformationFilters,
    AiTransformationPickBounds,
} from '../../../fetchers/position-management/ai-transformation-fetchers';

export const STRATEGY_VIEWS = ['roles', 'levels', 'positions', 'tasks'] as const;
export type StrategyView = (typeof STRATEGY_VIEWS)[number];

export const STRATEGY_LAYOUTS = ['kanban', 'list'] as const;
export type StrategyLayout = (typeof STRATEGY_LAYOUTS)[number];

/** A rectangle picked on the matrix, by score and business impact; it always belongs to the view's grouping. */
export type StrategyPick = Extract<AiTransformationPickBounds, { readonly impactMin: number }>;

/**
 * Everything the page shows, read from and written to the URL. The selected options of every attribute filter share
 * one list, as the backend reads them. The list layout shows the opportunities, the matrix and the list under it; the
 * kanban layout shows the progress board.
 */
export type StrategyViewState = Pick<
    AiTransformationFilters,
    'attributeValueId' | 'jobClusterId' | 'jobFamilyId' | 'levelId' | 'q' | 'roleId' | 'scoreLevel'
> & {
    readonly architectureId: string | undefined;
    readonly layout: StrategyLayout;
    readonly pick: StrategyPick | undefined;
    readonly view: StrategyView;
};

const DEFAULT_VIEW: StrategyView = 'roles';
const DEFAULT_LAYOUT: StrategyLayout = 'list';
const MAX_QUERY_LENGTH = 200;

export const DEFAULT_STRATEGY_VIEW_STATE: StrategyViewState = {
    architectureId: undefined,
    attributeValueId: [],
    jobClusterId: [],
    jobFamilyId: [],
    layout: DEFAULT_LAYOUT,
    levelId: [],
    pick: undefined,
    q: undefined,
    roleId: [],
    scoreLevel: [],
    view: DEFAULT_VIEW,
};

export const STRATEGY_SEARCH_PARAMS = [
    'architectureId',
    'attributeValueId',
    'jobClusterId',
    'jobFamilyId',
    'layout',
    'levelId',
    'pickImpactMax',
    'pickImpactMin',
    'pickPeopleShareMax',
    'pickPeopleShareMin',
    'pickScoreMax',
    'pickScoreMin',
    'q',
    'roleId',
    'scoreLevel',
    'view',
] as const;

const viewSchema = z.enum(STRATEGY_VIEWS).catch(DEFAULT_VIEW);
const layoutSchema = z.enum(STRATEGY_LAYOUTS).catch(DEFAULT_LAYOUT);
const shareSchema = z.coerce.number().min(0).max(1);
const pickSchema = z
    .object({ impactMax: shareSchema, impactMin: shareSchema, scoreMax: shareSchema, scoreMin: shareSchema })
    .refine(({ impactMax, impactMin, scoreMax, scoreMin }) => scoreMin <= scoreMax && impactMin <= impactMax);

const idsOf = (params: URLSearchParams, name: string): readonly string[] => [
    ...new Set(
        params
            .getAll(name)
            .map((value) => value.trim())
            .filter(is.nonEmptyString),
    ),
];

const textOf = (params: URLSearchParams, name: string, maxLength?: number): string | undefined => {
    const value = params.get(name)?.trim().slice(0, maxLength);

    return is.nonEmptyString(value) ? value : undefined;
};

/**
 * The score and impact bounds: a partial, out of range or inverted rectangle reads as no pick, as does an hours pick
 * from a link made while the matrix could plot the hours.
 */
const pickOf = (params: URLSearchParams): StrategyPick | undefined =>
    pickSchema.safeParse({
        impactMax: textOf(params, 'pickImpactMax'),
        impactMin: textOf(params, 'pickImpactMin'),
        scoreMax: textOf(params, 'pickScoreMax'),
        scoreMin: textOf(params, 'pickScoreMin'),
    }).data;

/** Reads the page state from the URL; values the page does not know fall back to their defaults. */
export const parseStrategyViewState = (params: URLSearchParams): StrategyViewState => ({
    architectureId: textOf(params, 'architectureId'),
    attributeValueId: idsOf(params, 'attributeValueId'),
    jobClusterId: idsOf(params, 'jobClusterId'),
    jobFamilyId: idsOf(params, 'jobFamilyId'),
    layout: layoutSchema.parse(params.get('layout') ?? undefined),
    levelId: idsOf(params, 'levelId'),
    pick: pickOf(params),
    q: textOf(params, 'q', MAX_QUERY_LENGTH),
    roleId: idsOf(params, 'roleId'),
    scoreLevel: [...new Set(params.getAll('scoreLevel'))].flatMap(
        (value) => automationScoreLevelSchema.safeParse(value).data ?? [],
    ),
    view: viewSchema.parse(params.get('view') ?? undefined),
});

/** Writes the page state as search params, leaving out every value that equals its default. */
export const serializeStrategyViewState = (state: StrategyViewState): URLSearchParams => {
    const params = new URLSearchParams();
    const set = (name: string, value: string | undefined, defaultValue?: string): void => {
        if (value !== undefined && value !== defaultValue) params.set(name, value);
    };
    const append = (name: string, values: readonly string[]): void => {
        for (const value of values) params.append(name, value);
    };
    const setBound = (name: string, value: number | undefined): void => set(name, value?.toString());

    set('architectureId', state.architectureId);
    set('view', state.view, DEFAULT_VIEW);
    set('layout', state.layout, DEFAULT_LAYOUT);
    append('jobFamilyId', state.jobFamilyId);
    append('jobClusterId', state.jobClusterId);
    append('roleId', state.roleId);
    append('levelId', state.levelId);
    append('attributeValueId', state.attributeValueId);
    append('scoreLevel', state.scoreLevel);
    set('q', state.q);
    setBound('pickScoreMin', state.pick?.scoreMin);
    setBound('pickScoreMax', state.pick?.scoreMax);
    setBound('pickImpactMin', state.pick?.impactMin);
    setBound('pickImpactMax', state.pick?.impactMax);

    return params;
};

const GROUPING_OF_VIEW: Readonly<Record<StrategyView, AiTransformationMatrixGrouping>> = {
    levels: 'LEVEL',
    positions: 'POSITION',
    roles: 'ROLE',
    tasks: 'TASK',
};

/** What the counts, board and list show: tasks in the tasks view, else positions. */
export const subjectOf = (view: StrategyView): AiTransformationSubject => (view === 'tasks' ? 'TASK' : 'POSITION');

export const groupingOf = (view: StrategyView): AiTransformationMatrixGrouping => GROUPING_OF_VIEW[view];

/** The page's filters without its pick, as the role and level tile lists that role's or level's positions. */
export const toQueryFiltersWithoutPick = ({
    attributeValueId,
    jobClusterId,
    jobFamilyId,
    levelId,
    q,
    roleId,
    scoreLevel,
}: StrategyViewState): AiTransformationFilters => ({
    attributeValueId,
    jobClusterId,
    jobFamilyId,
    levelId,
    q,
    roleId,
    scoreLevel,
});

const toQueryFilters = (state: StrategyViewState): AiTransformationFilters => {
    const filters = toQueryFiltersWithoutPick(state);

    return state.pick === undefined
        ? filters
        : { ...filters, pick: { ...state.pick, grouping: groupingOf(state.view) } };
};

/**
 * The inputs of every query on the page, all built on one filter object so they always show the same items. The pick
 * narrows the counts, the board and the list; the matrix gets it only to mark its picked points.
 */
export const strategyQueryInputs = (state: StrategyViewState) => {
    const filters = toQueryFilters(state);
    const subject = subjectOf(state.view);

    return {
        columns: { filters, subject },
        list: { filters, grouping: groupingOf(state.view), subject },
        matrix: { filters, grouping: groupingOf(state.view) },
        summary: { filters, subject },
    } as const;
};

/** Merges a change into the state. A pick belongs to the view's grouping, so switching the view clears it. */
export const updateStrategyViewState = (
    state: StrategyViewState,
    change: Partial<StrategyViewState>,
): StrategyViewState => {
    const switchesView = change.view !== undefined && change.view !== state.view;

    return { ...state, ...(switchesView ? { pick: undefined } : {}), ...change };
};

/**
 * The selected attribute values the filters still list. The backend refuses a value of no listed attribute, so one
 * that is gone, as from an old link, would fail every read.
 */
export const listedAttributeValueIds = (
    ids: readonly string[],
    attributes: readonly AiTransformationAttributeFilter[],
): readonly string[] => {
    const listed = new Set(attributes.flatMap(({ options }) => options.map(({ id }) => id)));

    return ids.filter((id) => listed.has(id));
};

/** The page's tab a layout shows: the progress board for the kanban, the opportunities otherwise. */
export const tabOf = ({ layout }: Pick<StrategyViewState, 'layout'>): 'opportunities' | 'progress' =>
    layout === 'kanban' ? 'progress' : 'opportunities';
